import gql from "graphql-tag";
import React, { useMemo, useCallback } from "react";
import { GraphqlOperations } from "@src/types.generated";
import useUser from "@src/features/app/hooks/useUser";
import {
  AdminToolsPanel,
  AdminToolsEmptyRow,
  AdminToolsRowList,
  AdminToolsToggleRow,
} from "@src/features/app/top-nav/AdminToolsLayout";
import {
  useOrgFeatureFlagsQuery,
  useSetOrgFeatureFlags_FeatureFlagsQuery,
  useUpdateOrgFeatureFlagsMutation,
} from "./SetOrgFeatureFlags.generated";

const SetOrgFeatureFlags: React.FC = () => {
  const { isStaff } = useUser();
  const { data: orgFeatureFlagsData } = useOrgFeatureFlagsQuery();

  const allFlags = useSetOrgFeatureFlags_FeatureFlagsQuery();
  const updatableFlags = useMemo(() => {
    if (!allFlags.data?.FeatureFlags) return [];
    return allFlags.data.FeatureFlags.filter((f) => f.staffSelfServableForOrg);
  }, [allFlags.data?.FeatureFlags]);

  const [updateFlagsMutation] = useUpdateOrgFeatureFlagsMutation({
    refetchQueries: [GraphqlOperations.Query.OrgFeatureFlags],
  });

  const updateFlags = useCallback(
    (name: string, enabled: boolean) => {
      updateFlagsMutation({
        variables: {
          input: {
            featureFlags: [
              {
                name,
                enabled: { value: enabled },
              },
            ],
          },
        },
        refetchQueries: [
          GraphqlOperations.Query.OrgFeatureFlags,
          GraphqlOperations.Query.FeatureFlags,
        ],
      });
    },
    [updateFlagsMutation],
  );

  const orgFlags: Record<string, boolean> = useMemo(() => {
    if (!orgFeatureFlagsData?.OrgFeatureFlags)
      return {} as Record<string, boolean>;
    return orgFeatureFlagsData.OrgFeatureFlags.reduce(
      (acc, f) => {
        acc[f.name] = f.enabled;
        return acc;
      },
      {} as Record<string, boolean>,
    );
  }, [orgFeatureFlagsData?.OrgFeatureFlags]);

  const featureFlagToggleRows = useMemo(() => {
    return updatableFlags.map((f) => (
      <AdminToolsToggleRow
        key={f.name}
        title={f.userFacingName || f.name}
        checked={!!orgFlags[f.name]}
        onCheckedChange={(checked) => updateFlags(f.name, checked)}
      />
    ));
  }, [orgFlags, updateFlags, updatableFlags]);

  if (!isStaff) return null;

  return (
    <AdminToolsPanel>
      <AdminToolsRowList>
        {featureFlagToggleRows.length > 0 ? (
          featureFlagToggleRows
        ) : (
          <AdminToolsEmptyRow>No org level feature flags</AdminToolsEmptyRow>
        )}
      </AdminToolsRowList>
    </AdminToolsPanel>
  );
};

gql`
  mutation UpdateOrgFeatureFlags($input: UpdateOrgFeatureFlagsInput!) {
    updateOrgFeatureFlags(input: $input) {
      success
    }
  }

  query OrgFeatureFlags {
    OrgFeatureFlags {
      name
      enabled
    }
  }

  query SetOrgFeatureFlags_FeatureFlags {
    FeatureFlags {
      name
      enabled
      userFacingName
      staffSelfServableForOrg
    }
  }
`;

export default SetOrgFeatureFlags;
